import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BusinessIcon from "@mui/icons-material/Business";
import SchoolIcon from "@mui/icons-material/School";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  fetchAgencies,
  fetchAgencyMemberships,
  fetchCertificationsByUserIds,
  updateAgencyMembership,
  sendAgencyInvite,
  fetchAgencyRoleTypes,
} from "../../api/apiClient";
import { canPickAgency, defaultAgencyId } from "./agencySelection";
import { parseLocalDate } from "../../utils/dateUtils";
import { useTenant } from "../../contexts/TenantContext";
import { useActiveTenant } from "../../hooks/useActiveTenant";
import NoTenantSelected from "../../components/NoTenantSelected";

// ── helpers ───────────────────────────────────────────────────────────────

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = parseLocalDate(dateStr);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.ceil((d - now) / 86400000);
}

function certStatus(cert) {
  if (!cert) return "missing";
  const days = daysUntil(cert.expires);
  if (days === null) return "valid"; // on file and never expires
  if (days < 0) return "expired";
  if (days <= 30) return "expiring";
  return "valid";
}

function fmtDate(d) {
  if (!d) return "—";
  return parseLocalDate(d).toLocaleDateString();
}

// ── ApproveDialog ─────────────────────────────────────────────────────────────

function ApproveDialog({ open, mode = "approve", request, roleTypes, onApprove, onClose }) {
  const navigate = useNavigate();
  const [category, setCategory] = useState("");

  useEffect(() => {
    if (open) setCategory(request?.category || "");
  }, [open, request]);

  const name = request?.user?.fullName || request?.user?.name || request?.email || "member";
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: "var(--font-display)" }}>
        {mode === "role" ? `Change role for ${name}` : `Approve ${name}`}
      </DialogTitle>
      <DialogContent>
        {roleTypes.length === 0 && (
          <Alert severity="info" sx={{ mt: 1 }}>
            No roles are set up for this agency yet.{" "}
            <Button size="small" onClick={() => navigate("/agency/role-requirements")}>
              Set up roles
            </Button>
          </Alert>
        )}
        <FormControl fullWidth margin="normal">
          <InputLabel id="agency-role-label">Role</InputLabel>
          <Select
            labelId="agency-role-label"
            value={category}
            label="Role"
            onChange={(e) => setCategory(e.target.value)}
            inputProps={{ "data-testid": "agency-role-select" }}
          >
            {roleTypes.map((rt) => (
              <MenuItem key={rt.id} value={rt.name}>
                {rt.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary">
          Required certifications come from the role.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => onApprove({ category })}
          disabled={!category}
        >
          {mode === "role" ? "Save role" : "Approve"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main export ────────────────────────────────────────────────────────────────

export default function AgencyManagementHub() {
  const { user, isFeatureEnabled } = useTenant();
  const { tenantId: activeTenantId } = useActiveTenant();
  const navigate = useNavigate();

  const [agencies, setAgencies] = useState([]);
  const [selectedAgencyId, setSelectedAgencyId] = useState(null);
  const [, setStats] = useState({
    totalMembers: 0,
    pendingApprovals: 0,
    expiringSoon: 0,
    expired: 0,
  });
  const [loading, setLoading] = useState(true);

  // Member management state
  const [members, setMembers] = useState([]);
  const [approvedMemberships, setApprovedMemberships] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [certsByUser, setCertsByUser] = useState({});
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);
  const [dialogMode, setDialogMode] = useState("approve");
  const [roleTypes, setRoleTypes] = useState([]);
  const [actionError, setActionError] = useState(null);

  const agencyManagementEnabled = isFeatureEnabled("agencyManagement");
  const hasAccess = !!user?.roles?.some((r) =>
    ["AGENCY_ADMIN", "GLOBAL_ADMIN", "ADMIN"].includes(r),
  );

  // Fetch agencies for active tenant
  useEffect(() => {
    if (!activeTenantId || !hasAccess) {
      setLoading(false);
      return;
    }
    fetchAgencies(activeTenantId).then((list) => {
      const arr = Array.isArray(list) ? list : list?.content || [];
      setAgencies(arr);
      const initial = defaultAgencyId(arr, user);
      if (initial) {
        setSelectedAgencyId(initial);
      } else {
        // No agency to manage - ensure loading is set to false
        setLoading(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenantId, hasAccess]);

  const loadStats = useCallback(async () => {
    if (!activeTenantId || !selectedAgencyId) return;
    try {
      setLoading(true);

      const [mems, types] = await Promise.all([
        fetchAgencyMemberships(activeTenantId, selectedAgencyId),
        fetchAgencyRoleTypes(activeTenantId, selectedAgencyId),
      ]);
      setRoleTypes(types);
      const approved = mems.filter((m) => m.status === "APPROVED" && m.user);
      const pending = mems.filter((m) => m.status !== "APPROVED");

      setApprovedMemberships(approved);
      setMembers(approved.map((m) => m.user));
      setPendingRequests(pending);

      let expiringSoon = 0;
      let expired = 0;

      if (approved.length > 0) {
        const allCerts = await fetchCertificationsByUserIds(
          approved.map((m) => m.user.id),
        );

        const byUser = {};
        approved.forEach((m) => {
          byUser[m.user.id] = [];
        });
        allCerts.forEach((c) => {
          if (byUser[c.userId] !== undefined) byUser[c.userId].push(c);
        });
        setCertsByUser(byUser);

        // Calculate stats
        allCerts.forEach((c) => {
          const status = certStatus(c);
          if (status === "expiring") expiringSoon++;
          if (status === "expired") expired++;
        });
      }

      setStats({
        totalMembers: approved.length,
        pendingApprovals: pending.filter((p) => p.status === "PENDING").length,
        expiringSoon,
        expired,
      });
    } finally {
      setLoading(false);
    }
  }, [activeTenantId, selectedAgencyId]);

  useEffect(() => {
    if (selectedAgencyId) {
      loadStats();
    }
  }, [selectedAgencyId, loadStats]);

  // Handler functions
  async function handleApprove({ category }) {
    setActionError(null);
    try {
      await saveMembership({ category });
    } catch (e) {
      setActionError(e?.response?.data?.message || "Could not save the member's role.");
      return;
    }
    setApproveDialogOpen(false);
    setApproveTarget(null);
    loadStats();
  }

  function saveMembership({ category }) {
    return updateAgencyMembership(
      activeTenantId,
      selectedAgencyId,
      approveTarget.userId,
      {
        status: "APPROVED",
        category,
      },
    );
  }

  async function handleInvite() {
    if (!inviteEmail) return;
    setActionError(null);
    try {
      await sendAgencyInvite(activeTenantId, selectedAgencyId, inviteEmail);
    } catch (e) {
      setActionError(e?.response?.data?.message || "Could not send the invitation.");
      setInviteDialogOpen(false);
      return;
    }
    setInviteEmail("");
    setInviteDialogOpen(false);
    loadStats();
  }

  async function handleRemoveMember(userId) {
    await updateAgencyMembership(activeTenantId, selectedAgencyId, userId, {
      status: "REMOVED",
    });
    loadStats();
  }

  if (!agencyManagementEnabled) {
    return (
      <Container maxWidth="xl" sx={{ pb: 4 }}>
        <Card>
          <CardContent>
            <Alert severity="info">
              Agency management is not enabled for your tenant.
            </Alert>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (!user || !hasAccess) {
    return (
      <Container maxWidth="xl" sx={{ pb: 4 }}>
        <Card>
          <CardContent>
            <Alert severity="error">
              You do not have permission to view this page.
            </Alert>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (!activeTenantId) {
    return (
      <Container maxWidth="xl" sx={{ pb: 4 }}>
        <NoTenantSelected message="Select a training center from the top bar to manage agencies." />
      </Container>
    );
  }

  return (
    <>
      <Box
        sx={{
          backgroundColor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "grey.200",
          mb: 3,
        }}
      >
        <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/")}
            sx={{
              color: "text.secondary",
              mb: 2,
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            Back to Home
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, fontFamily: "var(--font-display)" }}>
            Agency Management
          </Typography>
          <Typography color="text.secondary">
            Manage your agency members, approvals, and class enrollments
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ pb: 4 }}>
        {actionError && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        )}
        {canPickAgency(user) && (
          <>
            {agencies.length > 1 && (
              <FormControl size="small" sx={{ mb: 3, minWidth: 260 }}>
                <InputLabel>Agency</InputLabel>
                <Select
                  value={selectedAgencyId || ""}
                  label="Agency"
                  onChange={(e) => setSelectedAgencyId(e.target.value)}
                >
                  {agencies.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {agencies.length === 1 && (
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 3 }}>
                {agencies[0].name}
              </Typography>
            )}
          </>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* ── Pending Invites (INVITED status) ── */}
            {pendingRequests.some((p) => p.status === "INVITED") && (
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 600, fontFamily: "var(--font-display)" }}
                  >
                    📧 Pending Invites
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Invitations sent but not yet accepted
                  </Typography>
                  <Stack spacing={1}>
                    {pendingRequests
                      .filter((p) => p.status === "INVITED")
                      .map((req, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 2,
                            bgcolor: "grey.50",
                            borderRadius: 1,
                          }}
                        >
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {req.email}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Invited {fmtDate(req.createdAt)}
                            </Typography>
                          </Box>
                          <Chip label="Pending" size="small" color="info" />
                        </Box>
                      ))}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* ── Pending Applications (PENDING status) ── */}
            {pendingRequests.some((p) => p.status === "PENDING") && (
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 600, fontFamily: "var(--font-display)" }}
                  >
                    ⏳ Pending Applications
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Membership applications waiting for approval
                  </Typography>
                  <Stack spacing={2}>
                    {pendingRequests
                      .filter((p) => p.status === "PENDING" && p.user)
                      .map((req, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 2,
                            bgcolor: "warning.50",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "warning.200",
                          }}
                        >
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {req.user?.fullName ||
                                req.user?.name ||
                                req.user?.email}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Applied {fmtDate(req.createdAt)}
                            </Typography>
                          </Box>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => {
                              setDialogMode("approve");
                              setApproveTarget(req);
                              setApproveDialogOpen(true);
                            }}
                          >
                            Approve
                          </Button>
                        </Box>
                      ))}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* ── Invite Button ── */}
            <Box sx={{ mb: 4 }}>
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={() => setInviteDialogOpen(true)}
              >
                Send Invitation
              </Button>
            </Box>

            {/* ── Invite Dialog ── */}
            <Dialog
              open={inviteDialogOpen}
              onClose={() => setInviteDialogOpen(false)}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle sx={{ fontFamily: "var(--font-display)" }}>Send Agency Invitation</DialogTitle>
              <DialogContent>
                <TextField
                  autoFocus
                  margin="dense"
                  label="Email address"
                  type="email"
                  fullWidth
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setInviteDialogOpen(false)}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleInvite}>
                  Send Invite
                </Button>
              </DialogActions>
            </Dialog>

            {/* ── Approve Dialog ── */}
            <ApproveDialog
              open={approveDialogOpen}
              mode={dialogMode}
              request={approveTarget}
              roleTypes={roleTypes}
              onApprove={handleApprove}
              onClose={() => {
                setApproveDialogOpen(false);
                setApproveTarget(null);
              }}
            />

            <Divider sx={{ my: 4 }} />

            {/* ── Current Members List ── */}
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, fontFamily: "var(--font-display)" }}>
              👥 Active Members ({members.length})
            </Typography>

            <TableContainer component={Paper} sx={{ mb: 4 }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: "grey.100" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      Certifications
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {members.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">
                          No active members yet
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    members.map((member) => {
                      const membership = approvedMemberships.find(
                        (m) => m.user?.id === member.id,
                      );
                      const memberCerts = certsByUser[member.id] || [];
                      const certStatuses = memberCerts.map((c) =>
                        certStatus(c),
                      );
                      const hasExpired = certStatuses.includes("expired");
                      const hasExpiring = certStatuses.includes("expiring");

                      return (
                        <TableRow
                          key={member.id}
                          sx={{
                            "&:hover": { bgcolor: "grey.50" },
                            bgcolor: hasExpired
                              ? "error.50"
                              : hasExpiring
                                ? "warning.50"
                                : "transparent",
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {member.fullName || member.name || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {member.email}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label="Active"
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Typography variant="body2">
                                {membership?.category || "—"}
                              </Typography>
                              <IconButton
                                size="small"
                                aria-label={`Change role for ${member.name || member.email}`}
                                data-testid={`change-role-${member.id}`}
                                onClick={() => {
                                  setDialogMode("role");
                                  setApproveTarget({ ...membership, userId: member.id });
                                  setApproveDialogOpen(true);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            {memberCerts.length === 0 ? (
                              <Chip
                                label="None"
                                size="small"
                                color="default"
                                variant="outlined"
                              />
                            ) : (
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 0.5,
                                  flexWrap: "wrap",
                                }}
                              >
                                {hasExpired && (
                                  <Chip
                                    label={`${certStatuses.filter((s) => s === "expired").length} Expired`}
                                    size="small"
                                    color="error"
                                  />
                                )}
                                {hasExpiring && (
                                  <Chip
                                    label={`${certStatuses.filter((s) => s === "expiring").length} Expiring`}
                                    size="small"
                                    color="warning"
                                  />
                                )}
                                {!hasExpired &&
                                  !hasExpiring &&
                                  memberCerts.length > 0 && (
                                    <Chip
                                      label={`${memberCerts.length} Valid`}
                                      size="small"
                                      color="success"
                                    />
                                  )}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="error"
                              aria-label={`Remove ${member.name || member.email} from agency`}
                              onClick={() => handleRemoveMember(member.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 4 }} />

            {/* ── Additional Pages Navigation ── */}
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontFamily: "var(--font-display)" }}>
              Other Agency Tools
            </Typography>

            <Grid container spacing={3}>
              {/* Class Enrollments */}
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": { boxShadow: 3, transform: "translateY(-4px)" },
                  }}
                  onClick={() => navigate("/agency/class-enrollments")}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <SchoolIcon
                        sx={{ fontSize: 32, color: "info.main", mr: 1.5 }}
                      />
                      <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: "var(--font-display)" }}>
                        Class Enrollments
                      </Typography>
                    </Box>
                    <Typography
                      color="text.secondary"
                      variant="body2"
                      sx={{ mb: 2 }}
                    >
                      View your agency members' class enrollments and bulk
                      register a group of members for any available training
                      course.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Role Requirements */}
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": { boxShadow: 3, transform: "translateY(-4px)" },
                  }}
                  onClick={() => navigate("/agency/role-requirements")}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <AssignmentIcon
                        sx={{ fontSize: 32, color: "secondary.main", mr: 1.5 }}
                      />
                      <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: "var(--font-display)" }}>
                        Role Requirements
                      </Typography>
                    </Box>
                    <Typography
                      color="text.secondary"
                      variant="body2"
                      sx={{ mb: 2 }}
                    >
                      Define which certifications are required for each member
                      role/category. Manage role definitions and bulk-update
                      certification requirements.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Agency Details */}
              <Grid item xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": { boxShadow: 3, transform: "translateY(-4px)" },
                  }}
                  onClick={() => navigate("/agency/details")}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <BusinessIcon
                        sx={{ fontSize: 32, color: "primary.main", mr: 1.5 }}
                      />
                      <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: "var(--font-display)" }}>
                        Agency Details
                      </Typography>
                    </Box>
                    <Typography
                      color="text.secondary"
                      variant="body2"
                      sx={{ mb: 2 }}
                    >
                      Update counties served, task force memberships, and
                      notification preferences for your agency.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />
          </>
        )}
      </Container>
    </>
  );
}
